import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../lib/AuthContext";
import { UserPlus, Trash2, Loader2, ShieldCheck, Eye } from "lucide-react";

export default function Team() {
  const { user: me, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "viewer" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.listUsers().then((d) => setUsers(d.users || [])).catch(() => {});
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="bm-card p-8 max-w-lg mx-auto mt-16 text-center" data-testid="team-access-denied">
        <ShieldCheck className="w-8 h-8 text-bm-danger mx-auto" />
        <div className="font-heading font-black text-2xl mt-3">ACCESS DENIED</div>
        <p className="text-sm text-bm-muted mt-2 font-mono">Team management requires the admin role. Contact your administrator.</p>
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    try {
      await api.createUser(form);
      setForm({ email: "", name: "", password: "", role: "viewer" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this user's access?")) return;
    try { await api.deleteUser(id); load(); } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleRole = async (u) => {
    try { await api.updateUser(u.id, { role: u.role === "admin" ? "viewer" : "admin" }); load(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4">
        <div className="label-mono text-bm-amber">ACCESS CONTROL</div>
        <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">Team access.</h1>
        <p className="text-sm text-bm-muted mt-2 max-w-3xl">
          Invite-only. Admins can crawl, generate concepts and issue RFPs. Viewers get read-only dashboards.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <form onSubmit={create} className="bm-card p-5 space-y-3 self-start" data-testid="create-user-form">
          <div className="label-mono text-bm-cyan flex items-center gap-2"><UserPlus className="w-3 h-3" />INVITE USER</div>
          <div>
            <label className="label-mono text-bm-dim">Email</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="new-user-email"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none focus:border-bm-cyan" />
          </div>
          <div>
            <label className="label-mono text-bm-dim">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="new-user-name"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none focus:border-bm-cyan" />
          </div>
          <div>
            <label className="label-mono text-bm-dim">Password (min 8 chars)</label>
            <input type="text" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="new-user-password"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none focus:border-bm-cyan" />
          </div>
          <div>
            <label className="label-mono text-bm-dim">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="new-user-role"
              className="w-full bg-bm-elevated border border-white/10 px-3 py-2 mt-1 font-mono text-sm text-white outline-none">
              <option value="viewer">viewer — read-only</option>
              <option value="admin">admin — full control</option>
            </select>
          </div>
          {error && <div className="text-xs font-mono text-bm-danger" data-testid="team-error">{error}</div>}
          <button type="submit" disabled={busy} data-testid="create-user-btn"
            className="w-full bg-bm-green text-black font-heading font-bold py-2.5 hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "CREATE ACCOUNT"}
          </button>
        </form>

        <div className="bm-card p-5 lg:col-span-2" data-testid="users-list">
          <div className="label-mono text-bm-cyan mb-4">ACTIVE ACCOUNTS · {users.length}</div>
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 border border-white/5 px-3 py-2.5" data-testid={`user-row-${u.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {u.role === "admin"
                    ? <ShieldCheck className="w-4 h-4 text-bm-amber shrink-0" />
                    : <Eye className="w-4 h-4 text-bm-cyan shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm text-white font-mono truncate">{u.email}{u.id === me?.id && <span className="text-bm-dim"> (you)</span>}</div>
                    <div className="label-mono text-bm-dim">{u.name || "—"} · {u.role}{u.is_active === false && " · DEACTIVATED"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {u.id !== me?.id && (
                    <>
                      <button onClick={() => toggleRole(u)} data-testid={`toggle-role-${u.id}`}
                        className="px-2 py-1 text-[10px] font-mono border border-white/10 text-bm-muted hover:border-bm-amber hover:text-bm-amber transition-colors">
                        MAKE {u.role === "admin" ? "VIEWER" : "ADMIN"}
                      </button>
                      <button onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`}
                        className="p-1.5 border border-white/10 text-bm-dim hover:border-bm-danger hover:text-bm-danger transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
